import type { HighchartExportTypes, HighchartsSeriesTypes } from '@/models/common/chart/ChartTypes';
import type { RegressionOptions } from '@/models/common/RegressionOptions';
import type { Point, PointOptionsObject, Series, SeriesLineOptions, TooltipFormatterCallbackFunction } from 'highcharts';
import { Chart } from 'highcharts';
import { i18n } from '@/i18n';
import { ChartColors } from '@/models/common/ChartColors';
import 'highcharts/modules/exporting';
import 'highcharts/modules/offline-exporting';
import regression from 'regression';

/**
 * Default tooltipformatter for chart tooltip with tabular data.
 * @returns A function to format the tooltip html.
 */
export function tooltipCallbackFunc(): TooltipFormatterCallbackFunction {
    return function () {
        const { point } = this;
        let tooltip = '<table class="sy-highcharts-tooltip-table">';
        tooltip += `<tr>
                <th colspan="2" class="sy-type-small-bold padding-top-3 padding-right-5 text-left sy-border-bottom">${point.custom?.tooltipHeader}</th>
            </tr>`;
        point.custom?.tooltipData?.forEach((data, index) => {
            tooltip += `
            <tr>
                <td class=" ${index === 0 ? 'padding-top-3' : ''} padding-right-5">${data.title}</td>
                <td class="text-right ${index === 0 ? 'padding-top-3' : ''} sy-break-word">${data.value}</td>
            </tr>
            `;
        });
        tooltip += '</table>';
        return tooltip;
    };
}

/**
 * Get all points with the same y value as a point inside chart.
 * @param point Point to check proximity to.
 * @returns <Point[]> Point data array of all points in proximity
 */
export function getPointsByProximity(point: Point): Point[] {
    const points: Point[] = [];
    point.series.chart.series.forEach(
        (serie: Series) => {
            if (serie.type === 'scatter' && serie.data != null) {
                // Create array with all series that contains correct category and y value
                serie!.data!.forEach(data => {
                    // check if the x and y value within 0.5% of the point and that the y axis is the same
                    if (data?.y != null && point.y != null && data.y >= point.y * 0.995 && data.y <= point.y * 1.005 && data.x === point.x) {
                        points.push(data);
                    }
                });
            }
        }
    );
    return points;
}

/**
 * Export function for chart.
 * @param type Document type to export.
 * @param chartInstance The chart instance.
 * @param title Optional title for the export.
 */
export function onExportChart(type: HighchartExportTypes, chartInstance: Chart, title?: string): void {
    // clone the chartInstance
    const temporaryChartContainer = document.createElement('div');
    const generatedChart = new Chart(temporaryChartContainer, chartInstance.userOptions);

    // capitalize every word in the title
    const filename = title != null ? title.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join('') : 'diagram';

    // Export the chart locally
    generatedChart.exportChartLocal({
        type,
        filename,
        scale: 2,
        sourceWidth: 1500,
        sourceHeight: 1000
    });

    generatedChart.destroy();
}

/**
 * Default colors to be used on chart series
 * @param index index of the current serie.
 * @returns returns a hexstring from an array of colors in ChartColors.
 */
export function getDefaultSeriesColor(index: number): string {
    return ChartColors[index % ChartColors.length];
}

/**
 * Calculate regression Standard error between points
 * @param yValues Y values from points
 * @param predictedYValues Y values from points after prediction
 * @returns Standard error value
 */
export function calculateStandardError(yValues: number[], predictedYValues: number[]): number {
    if (yValues.length !== predictedYValues.length) {
        throw new Error('The input arrays must have the same length.');
    }

    const squaredResidualsSum = yValues.reduce((sum, y, i) => {
        const residual = y - predictedYValues[i];
        return sum + residual ** 2;
    }, 0);

    const standardError = Math.sqrt(squaredResidualsSum / (yValues.length - 2));

    return standardError;
}

/**
 * Create regression line options.
 * @param regressionOptions Regression Options
 * @param serie Serie to create regression data from.
 * @returns Regression line settings.
 */
export function createRegressionLine(regressionOptions: RegressionOptions, serie: HighchartsSeriesTypes, pointData: [x: number, y: number][]): SeriesLineOptions | null {
    // If all values has the same x value don't create a line.
    const yIndex = 1;
    const xIndex = 0;
    const xValues = new Set<number>(pointData.map(x => x[xIndex]));
    if (xValues.size === 1) return null;

    // Sort the pointData
    pointData.sort((a, b) => a[xIndex] - b[xIndex]);

    // Create linear regression
    const { r2, predict, string, points } = regression.linear(pointData, { precision: 3 });

    // Get the predicted y values from the x value
    const predictedValues: number[] = pointData.map(x => predict(x[xIndex])[yIndex]);

    // Calculate the standard error
    const standardError = calculateStandardError(pointData.map(x => x[yIndex]), predictedValues);

    const translatedR = i18n.global.n(Number.isNaN(r2) ? 0 : Math.sqrt(r2), 'decimal');
    const translateR2 = i18n.global.n(Number.isNaN(r2) ? 0 : r2, 'decimal');

    // Create regression line data points
    const regressionData: PointOptionsObject[] = points.map(point => ({ x: point[xIndex], y: point[yIndex] }));

    if (points.length > 2) {
        regressionData[0].dataLabels = {
            enabled: regressionOptions.showSE,
            formatter(): string {
                return `SE: ${i18n.global.n(standardError, 'decimal')}`;
            }
        };
    }

    if ((regressionOptions.showR || regressionOptions.showR2) && points.length > 2) {
        regressionData[points.length - 1].dataLabels = {
            enabled: true,
            formatter(): string {
                if (regressionOptions.showR2 && regressionOptions.showR) {
                    return `r: ${translatedR} | r²: ${translateR2}`;
                }

                if (regressionOptions.showR) {
                    return `r: ${translatedR}`;
                }

                if (regressionOptions.showR2) {
                    return `r²: ${translateR2}`;
                }
                return '';
            }
        };
    }

    const regressionLine: SeriesLineOptions = {
        name: `${i18n.global.t('components.chart.regression.regressionAnalysis')} - ${serie.name}${regressionOptions.showEQ ? ` | ${string}` : ''}`,
        dashStyle: 'Dash',
        color: serie.color,
        type: 'line',
        animation: false,
        id: `${serie.name}-regression`,
        visible: regressionOptions.showLinearRegression,
        showInLegend: regressionOptions.showLinearRegression,
        states: { hover: { enabled: false } },
        marker: { enabled: false, radius: 0 },
        enableMouseTracking: false,
        data: regressionData
    };
    return regressionLine;
}

/**
 * Series draggable options for chart series.
 * @param dragMinY Min value for draggin in y direction
 * @param dragMaxY Max value for draggin in y direction
 * @returns Chart series dragabble options.
 */
export function setDraggableY(dragMinY: number = 0, dragMaxY?: number): any {
    return {
        dragDrop: {
            dragMinY,
            dragMaxY,
            draggableY: true,
            dragPrecisionY: 100,
            dragSensitivity: 2
        },
        animation: {
            duration: 0
        },
    };
}

/**
 * Gets the appropriate symbol character based on Highcharts marker symbol name or chart type.
 * @param markerSymbol - The Highcharts marker symbol name.
 * @param chartType - The chart type.
 * @returns The symbol character to use.
 */
export function getSymbolForMarker(markerSymbol: string | undefined, chartType: string | undefined): string {
    if (markerSymbol != null) {
        switch (markerSymbol.toLowerCase()) {
            case 'circle':
                return '●';
            case 'square':
                return '■';
            case 'diamond':
                return '◆';
            case 'triangle':
                return '▲';
            case 'triangle-down':
                return '▼';
        }
    }

    if (chartType == null) {
        return '■';
    }

    switch (chartType.toLowerCase()) {
        case 'boxplot':
        case 'scatter':
            return '●';
        case 'line':
            return '─';
        default:
            return '■';
    }
}
